import { IconButton, Link, Box, Typography } from '@material-ui/core';
import { flowResult } from 'mobx';
import {
  ArrowForward,
  AssignmentTurnedIn,
  Cancel,
  CheckCircle,
  ClosedCaption,
  Delete,
  Edit,
  FindInPageOutlined,
  GetApp,
  History,
  Info,
  Launch,
  Publish,
  Redo,
  Search,
  Undo,
  Check,
  Clear,
  ArrowUpwardRounded,
  PhotoCamera,
} from '@material-ui/icons';
import { PostClosingConditionMenuIcon } from '../../../icons/PostClosingConditionMenuIcon';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { GlobalStore, useBaseStore, useUserStore } from '@roc/feature-app-core';
import {
  DocumentName,
  DONT_DELETE_EXCLUDE_DOCUMENTS,
  useDocumentStore,
  ValuationDisputeService,
} from '@roc/feature-documents';
import { CompletionNoteDialog } from '@roc/feature-general-tasks';
import { useLoanStore } from '@roc/feature-loans';
import { Document } from '@roc/feature-types';
import { insertIf, isNil, LoanProcess, shouldHideTaskUploadTab } from '@roc/feature-utils';
import {
  AlertDialog,
  ResolveClosingConditionDialog,
  InformationDialog,
  CreateClosingConditionDialog,
  CreatePostClosingConditionDialog,
  DropdownMenu,
  FileUpload,
  FileUploadModal,
  ConfirmDialog,
  SelectField,
} from '@roc/ui';
import { DocumentHistoryDialog } from 'libs/feature-documents/src/documents/components/documentHistory/documentHistoryDialog';
import { isFileAlreadyUploaded } from 'libs/feature-documents/src/documents/utils/documents';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { DeleteModal } from '../common/deleteModal';
import { RenameModal } from '../common/renameModal';
import { MoveDocumentModal } from '../common/moveDocumentModal';
import { areActionsUnavailableBasedOnDocument, AssignToDialog, AssignToOwnerDialog, CustomTaskDialog, SingleTaskView, TASK_IN_PROGRESS_MESSAGE, TaskStatus, TaskV2Type, TaskWorkflowStatus, useGeneralTasksStore } from '@roc/feature-general-tasks';
import { useLoanFlags } from '../../hooks/useLoanFlags';
import { DRAWS_PROCESS } from 'libs/feature-draws/src/draws/constants/draws';
import { todoStatusMapping } from '../../utils/loanDetailsConstants';
import { LineItemUpload } from '../lineItemUpload';
import { TodoStatus } from '../../utils/loanDetailsTypes';
import { LoanTodoStore } from 'libs/feature-loans-shared/src/stores/loanTodoStore';

enum CONFIRM_MODAL_ACTION {
  ASSIGN_TO_OWNER,
  MARK_AS_COMPLETED,
  MARK_AS_NOT_APPLICABLE,
  ESCALATE_TASK,
};

const useStyles = makeStyles((theme: Theme) => ({
  link: {
    color: theme.palette.text.primary,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  downloadTodo: {
    color: '#86b8ff',
    margin: theme.spacing(-1.5, 0),
  },
  informationDialogText: {
    margin: '-32px',
  },
}));

const showOpen = (document: Document, globalStore: GlobalStore) => {
  if (document.taskName === DocumentName.TITLE_COMPANY_BANK_INFORMATION && globalStore.isInternalPortal) {
    return !!globalStore.userFeatures?.closingEntitlements?.isTitleCompanyBankInfoReviewAllowed;
  }
  if (document.taskName === DocumentName.INSURANCE_DOCUMENT) {
    return true;
  }
  return (
    document.isForm &&
    (document.canOpenForm ||
      [
        DocumentName.DRAW_REPORT,
        DocumentName.CLOSING_CONDITION_SUFIX,
        DocumentName.BACKGROUND_REPORT,
      ].includes(
        (document.taskName as any)
      ))
  );
};

const showDownload = document => {
  return document.canDownloadFile;
};

const getRealValPhotoSourceTaskCodes = (): string[] => [
  DocumentName.TAKE_INTERNAL_VALUATION_PHOTOS,
  DocumentName.TAKE_INTERNAL_VALUATION_PHOTOS_MANUAL,
  DocumentName.TAKE_INTERNAL_VALUATION_PHOTOS_APPRAISAL,
];

const REALVAL_PHOTO_SOURCE_OPTIONS = [
  { value: 'MOBILE', label: 'Mobile App', description: 'Borrower takes the photos with the Roc mobile app (default).' },
  { value: 'MANUAL', label: 'Manual Upload', description: 'Photos are uploaded from a computer in the "RealVal Property Photos (Manual)" item.' },
  { value: 'APPRAISAL', label: 'Appraisal Report', description: 'An appraisal report PDF is uploaded in the "RealVal Property Photos (Appraisal)" item.' },
];

const ChangeRealValPhotoSourceDialog = observer(({ open, onClose, document, typeId2 }: { open: boolean; onClose: () => void; document: Document; typeId2?: number }) => {
  const { generalTasksStore } = useGeneralTasksStore();
  const { globalStore } = useBaseStore();
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const propertyId = document.collateralId ?? typeId2;

  useEffect(() => {
    if (open) {
      if (!propertyId) {
        globalStore.notificationStore.showErrorNotification({
          message: 'Unable to load RealVal photo source: property ID is missing.',
        });
        return;
      }
      setLoading(true);
      flowResult(generalTasksStore.getRealValPhotoSource(document.loanId, propertyId)).then((data: any) => {
        setSource(data?.source || 'MOBILE');
        setLoading(false);
      });
    }
  }, [open]);

  const handleConfirm = async () => {
    if (loading || !source) {
      return;
    }
    if (!propertyId) {
      globalStore.notificationStore.showErrorNotification({
        message: 'Unable to change RealVal photo source: property ID is missing.',
      });
      return;
    }
    await flowResult(generalTasksStore.changeRealValPhotoSource(document.loanId, propertyId, source, onClose));
  };

  return (
    <ConfirmDialog
      open={open}
      title="Change RealVal Photo Source"
      handleClose={onClose}
      handleCancel={onClose}
      handleConfirm={handleConfirm}
      body={
        loading ? (
          <Typography variant="body2">Loading...</Typography>
        ) : (
          <SelectField
            testId="realValPhotoSource"
            fullWidth
            value={source}
            onChange={value => setSource(value)}
            options={REALVAL_PHOTO_SOURCE_OPTIONS}
          />
        )
      }
    />
  );
});

export const TodoActions = observer(({ document, loanProcess, onClose = undefined, fetchCallback = undefined, typeId2 = undefined }) => {
  const { documentStore } = useDocumentStore();
  const { isCombinedTaskFrameworkEnabled } = useLoanFlags();
  const { generalTasksStore } = useGeneralTasksStore();
  const { editTaskStore } = generalTasksStore;
  const [showHistory, setShowHistory] = useState(false);
  const [isUploadOpen, setUploadOpen] = useState(false);
  const [isChangeRealValSourceOpen, setChangeRealValSourceOpen] = useState(false);
  const [showLoanDocumentId, setShowLoanDocumentId] = useState(false);
  const [task, setTask] = useState(null);
  const { globalStore } = useBaseStore();
  const { userStore } = useUserStore();
  const { loanStore } = useLoanStore();
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isMoveOpen, setIsMoveOpen] = useState(false);
  const [isClosingConditionOpen, setIsClosingConditionOpen] = useState(false);
  const [isViewClosingConditionOpen, setIsViewClosingConditionOpen] = useState(false);
  const [isConfirmClosingConditionOpen, setIsConfirmClosingConditionOpen] = useState(false);
  const [isRejectClosingConditionOpen, setIsRejectClosingConditionOpen] = useState(false);
  const [isPostClosingConditionOpen, setIsPostClosingConditionOpen] = useState(false);
  const [isViewPostClosingConditionOpen, setIsViewPostClosingConditionOpen] = useState(false);
  const [isAcceptPostClosingConditionOpen, setIsAcceptPostClosingConditionOpen] = useState(false);
  const [isMoreInfoPostClosingConditionOpen, setIsMoreInfoPostClosingConditionOpen] = useState(false);
  const isInternalPortal = globalStore.isInternalPortal;
  const [confirmModalAction, setConfirmModalAction] = useState<CONFIRM_MODAL_ACTION>(null);
  const [informationModalText, setInformationModalText] = useState('');
  const [showAssignToModal, setShowAssignToModal] = useState<boolean>(false);
  const [showAssignToOwnerModal, setShowAssignToOwnerModal] = useState<boolean>(false);
  const [showCompletionNoteModal, setShowCompletionNoteModal] = useState<boolean>(false);
  const classes = useStyles();
  const actionsUnavailable = areActionsUnavailableBasedOnDocument(document);

  const requiresCompletionNote =
    document?.taskName === DocumentName.INTERNAL_VALUATION_ROV_FOR_APPRAISAL ||
    document?.taskName === DocumentName.CLIENT_VALUATION_DISPUTE;
  const valuationDisputeService = new ValuationDisputeService();

  const onCloseAction = () => {
    if (onClose) {
      onClose();
    }
    else {
      fetchTodos(document?.loanId, loanProcess);
    }
  }

  const onUpload = async (file, doc) => {
    await documentStore.uploadDocument(
      file,
      doc,
      loanProcess,
      null,
      document.drawId
    );
    onCloseAction();
  };

  const onOpenForm = (doc: Document) => {
    documentStore.onOpenForm(doc);
  };

  const onDownload = doc => documentStore.downloadDocument(doc);

  const onViewForm = () => {
    documentStore.openDrawReviewModal = true;
  };

  const onPreview = doc => documentStore.previewDocument(doc);

  const onShareLink = doc => documentStore.onShareLink(doc);

  const handleCloseTask = () => {
    setTask(null);
    fetchTodos(document?.loanId, loanProcess);
  }

  const handleCloseClosingCondition = () => {
    setIsClosingConditionOpen(false)
    fetchTodos(document?.loanId, loanProcess);
  }

  const handleCloseConfirmClosingCondition = () => {
    setIsConfirmClosingConditionOpen(false)
    fetchTodos(document?.loanId, loanProcess);
  }

  const handleCloseRejectClosingCondition = () => {
    setIsRejectClosingConditionOpen(false)
    fetchTodos(document?.loanId, loanProcess);
  }

  const handleConfirmCreateClosingCondition = () => {
    setIsClosingConditionOpen(false)
    fetchTodos(document?.loanId, loanProcess);
  }

  const handleOpenPostClosingCondition = () => {
    setIsPostClosingConditionOpen(true);
  }

  const handleClosePostClosingCondition = () => {
    setIsPostClosingConditionOpen(false);
    fetchTodos(document?.loanId, loanProcess);
  }

  const resolvePostClosingCondition = async (accepted: boolean, comment?: string) => {
    try {
      const postClosingConditionService = documentStore.documentFormStore.createPostClosingConditionStore.postClosingConditionService;
      await postClosingConditionService.resolveCondition(document?.taskId, accepted, comment);
      globalStore.notificationStore.showSuccessNotification({
        message: accepted ? 'Post-closing condition accepted.' : 'Post-closing condition rejected.',
      });
      setIsAcceptPostClosingConditionOpen(false);
      setIsMoreInfoPostClosingConditionOpen(false);
      fetchTodos(document?.loanId, loanProcess);
    } catch {
      globalStore.notificationStore.showErrorNotification({
        message: 'Error resolving post-closing condition.',
      });
    }
  }

  const fetchTodos = (loanId, loanProcess) => {
    if (loanProcess === LoanProcess.TODOS) {
      documentStore.loanTodosStore.fetchTodosForMultipleLoanProcesses(loanId, loanProcess, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING]);
    } else if (loanProcess === LoanProcess.PAYOFF) {
      documentStore.loanTodosStore.fetchTodos(loanId, loanProcess, TaskV2Type.PAYOFF_ID, document?.payoffId);
    } else {
      documentStore.loanTodosStore.fetchTodos(loanId, loanProcess, TaskV2Type.DRAW_ID, document?.drawId);
    }
  }

  const completeClosingCondition = (loanId: number, taskId: number, comment: string) => {
    documentStore.documentFormStore.loanClosingConditionStore.completeClosingCondition(loanId, taskId, comment, handleCloseConfirmClosingCondition);
  }

  const rejectClosingCondition = (loanId: number, taskId: number, comment: string) => {
    documentStore.documentFormStore.loanClosingConditionStore.rejectClosingCondition(loanId, taskId, comment, handleCloseConfirmClosingCondition);
  }

  const onRequestMoreInformation = (doc: Document) => {
    documentStore.requestMoreInformation(doc);
  };

  const downloadBorrowerPastLoanDocuments = doc =>
    documentStore.downloadBorrowerPastLoanDocuments(doc);

  const showRenameAndDelete =
    !!document.dropboxPath && globalStore.isInternalPortal && !DONT_DELETE_EXCLUDE_DOCUMENTS.includes(document.taskName);

  const handleOpenConfirmModal = (action: CONFIRM_MODAL_ACTION) => {
    if (actionsUnavailable) {
      setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
    } else if (action === CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED && requiresCompletionNote) {
      setShowCompletionNoteModal(true);
    } else {
      setConfirmModalAction(action);
    }
  }

  const handleCloseAssignToModal = () => {
    setShowAssignToModal(false);
    fetchTodos(document?.loanId, loanProcess);
    loanStore.fetchLoanDetails(document.loanId);
  }

  const handleCloseAssignToOwnerModal = () => {
    setShowAssignToOwnerModal(false);
    fetchTodos(document?.loanId, loanProcess);
  }

  const handleSubmitConfirmModal = () => {
    if (confirmModalAction != null) {

      if (confirmModalAction === CONFIRM_MODAL_ACTION.ESCALATE_TASK) {
        generalTasksStore.escalateTask(document.taskId, () => {
          if (fetchCallback) {
            fetchCallback();
          } else {
            fetchTodos(document?.loanId, loanProcess)
          }
        });

      } else {
        // status update cases

        let status;
        if (confirmModalAction === CONFIRM_MODAL_ACTION.ASSIGN_TO_OWNER) {
          if (document.status === TaskStatus.NOT_APPLICABLE) {
            status = TodoStatus.NOT_APPLICABLE;
          } else {
            status = TodoStatus.MORE_INFO_NEEDED;
          }
        } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED) {
          status = TodoStatus.ACCEPTED;
        } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_NOT_APPLICABLE) {
          status = TodoStatus.NOT_APPLICABLE;
        }
        documentStore.loanTodosStore.updateTodoStatusV2(
          document,
          status,
          () => fetchTodos(document.loanId, loanProcess)
        );
      }
    }
    setConfirmModalAction(null);
  }

  const getConfirmModalBody = () => {
    let action = '';

    if (confirmModalAction === CONFIRM_MODAL_ACTION.ASSIGN_TO_OWNER) {
      action = 'assign the task to the owner';
    } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED) {
      action = 'mark the task as completed';
    } else if (confirmModalAction === CONFIRM_MODAL_ACTION.MARK_AS_NOT_APPLICABLE) {
      action = 'mark the task as not applicable';
    } else if (confirmModalAction === CONFIRM_MODAL_ACTION.ESCALATE_TASK) {
      action = 'escalate this task'
    }

    return <Typography>Please confirm you want to {action}.</Typography>
  }

  const getDocumentActions = () => {
    const actionList = [];

    if (document.isForm) {
      if (document.taskName === DocumentName.DRAW_REPORT) {
        return [
          {
            icon: Launch,
            name: 'Open',
            action: () => onOpenForm(document),
            disabled: !(document.status === TaskStatus.ACCEPTED),
          },
        ];
      }

      if (
        document.taskName.includes(DocumentName.CLOSING_CONDITION_SUFIX)
      ) {
        return [
          {
            icon: Launch,
            name: 'Open',
            action: () => onOpenForm(document),
          },
          {
            icon: GetApp,
            name: 'Download',
            action: () => onDownload(document),
            disabled: !document.canDownloadFile,
          },
        ];
      }
      if (document.canOpenForm) {
        actionList.push({
          icon: Launch,
          name: 'Open',
          action: () => {
            if (document.taskName === DocumentName.INTERNAL_VALUATION_REPORT && document.readOnly && document.status === TaskStatus.NOT_APPLICABLE) {
              if (actionsUnavailable) {
                setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
              } else {
                setShowAssignToModal(true);
              }
            } else {
              onOpenForm(document)
            }
          }

        });
      }
      if (document.canRequestMoreInformation && userStore.isBackOfficeUser) {
        actionList.push({
          icon: FindInPageOutlined,
          name: 'Mark as More Info Needed',
          action: () => onRequestMoreInformation(document),
        });
      }
    } else {
      if (document.canUploadFile) {
        actionList.push({
          icon: Publish,
          name: 'Upload',
          action: () => {
            if (actionsUnavailable) {
              setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
            } else {
              setUploadOpen(true);
            }
          },
        });
      }
      if (document.canDownloadFile) {
        actionList.push({
          icon: GetApp,
          name: 'Download',
          action: () => onDownload(document),
        });
        actionList.push({
          icon: Search,
          name: 'Preview',
          action: () => onPreview(document),
        });
      }
      if (showRenameAndDelete) {
        actionList.push({
          icon: Edit,
          name: 'Rename',
          action: () => setIsRenameOpen(true),
        });
        actionList.push({
          icon: Delete,
          name: 'Delete',
          action: () => setIsDeleteOpen(true),
        });
        actionList.push({
          icon: ArrowForward,
          name: 'Move',
          action: () => setIsMoveOpen(true),
        });
      }
      if (document.taskName === DocumentName.DRAW_REQUEST_FORM) {
        return [
          {
            icon: Launch,
            name: 'Fill Form',
            action: () => {
              documentStore.openDrawRequestModal = true;
              documentStore.setCurrentDocument(document);
            },
            disabled: !(
              document.status === TaskStatus.MORE_INFO_NEEDED ||
              document.status === TaskStatus.PENDING
            ),
          },
          // {
          //   icon: Link,
          //   name: 'Share Link',
          //   action: () => onShareLink(document),
          // },
        ];
      }
      if (!isInternalPortal && document.taskName === DocumentName.PROPERTY_PHOTOS) {
        return [
          {
            icon: Link,
            name: 'Share Link',
            action: () => onShareLink(document),
          },
        ];
      }

      if (document.taskName === DocumentName.PLAID_BANKS) {
        return [
          ...insertIf(isFileAlreadyUploaded(document), [
            {
              icon: GetApp,
              name: 'Download Document',
              action: () => onDownload(document),
            },
          ]),
          ...insertIf(globalStore.userFeatures.plaidRegistration, [
            {
              icon: Link,
              name: 'Share Link',
              action: () => onShareLink(document),
            },
          ]),
        ];
      }
      if (
        globalStore.isInternalPortal &&
        document.taskName === DocumentName.BORROWER_PAST_LOAN_DOCUMENTS
      ) {
        actionList.push({
          icon: GetApp,
          name: 'Download Zip',
          action: () => downloadBorrowerPastLoanDocuments(document),
        });
      }
    }
    if (document.canDownloadFile) {
      actionList.push({
        icon: History,
        name: 'History',
        action: () => setShowHistory(true),
      });
    }
    if (globalStore.isInternalPortal && document.taskId) {
      if (!(document.closingCondition && document.status !== TaskStatus.ACCEPTED) &&
          !(document.postClosingCondition && document.status !== TaskStatus.ACCEPTED)) {
        if (!document.internalTask
          && document.taskStatus !== TaskStatus.PENDING
          && document.taskStatus !== TaskStatus.MORE_INFO_NEEDED
          && !document.readOnly) {
          actionList.push({
            icon: Undo,
            name: 'Assign to Sales',
            action: () => {
              if (actionsUnavailable) {
                setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
              } else {
                setShowAssignToOwnerModal(true);
              }
            },
          });
        }
        if (document.taskStatus !== TaskStatus.ACCEPTED && !document.readOnly) {
          actionList.push({
            icon: Check,
            name: 'Mark as Completed',
            action: () => handleOpenConfirmModal(CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED),
          });
        }
        if (document.taskStatus !== TaskStatus.NOT_APPLICABLE && !document.readOnly) {
          actionList.push({
            icon: Clear,
            name: 'Mark as Not Applicable',
            action: () => handleOpenConfirmModal(CONFIRM_MODAL_ACTION.MARK_AS_NOT_APPLICABLE),
          });
        }
        if (isNil(document.escalated)) {
          actionList.push({
            icon: ArrowUpwardRounded,
            name: 'Escalate Task',
            action: () => handleOpenConfirmModal(CONFIRM_MODAL_ACTION.ESCALATE_TASK),
          });
        }
      }

      if (!document.readOnly) {
        actionList.push({
          icon: Redo,
          name: 'Assign To',
          action: () => {
            if (actionsUnavailable) {
              setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
            } else {
              setShowAssignToModal(true);
            }
          },
        });
      }

      actionList.push({
        icon: AssignmentTurnedIn,
        name: 'Open Task',
        action: () => setTask(document?.taskId),
      });
    }
    if (document.taskId && !globalStore.isInternalPortal) {
      if (
        globalStore.userFeatures?.isClosingAttorney &&
        document.taskName === DocumentName.SIGNED_CLOSING_DOCUMENTS_REVIEW &&
        document.taskStatus !== TaskStatus.ACCEPTED &&
        !document.readOnly
      ) {
        actionList.push({
          icon: Check,
          name: 'Mark as Completed',
          action: () => handleOpenConfirmModal(CONFIRM_MODAL_ACTION.MARK_AS_COMPLETED),
        });
      }
      if (!(document.closingCondition && document.status !== TaskStatus.ACCEPTED) &&
          !(document.postClosingCondition && document.status !== TaskStatus.ACCEPTED) &&
          isNil(document.escalated)) {
        actionList.push({
          icon: ArrowUpwardRounded,
          name: 'Escalate Task',
          action: () => handleOpenConfirmModal(CONFIRM_MODAL_ACTION.ESCALATE_TASK),
        });
      }
    }
    if (document.stage !== DRAWS_PROCESS) {
      if (globalStore.isInternalPortal &&
        document.taskId &&
        !document.closingCondition &&
        isCombinedTaskFrameworkEnabled &&
        document.status !== TaskStatus.ACCEPTED) {
        actionList.push({
          icon: ClosedCaption,
          name: 'Create Closing Condition',
          action: () => setIsClosingConditionOpen(true),
        });
      }
      if (globalStore.isInternalPortal &&
        document.taskId &&
        !document.postClosingCondition &&
        isCombinedTaskFrameworkEnabled &&
        document.status !== TaskStatus.ACCEPTED) {
        actionList.push({
          icon: PostClosingConditionMenuIcon,
          name: 'Create Post-Closing Condition',
          action: () => handleOpenPostClosingCondition(),
        });
      }
      if (globalStore.isInternalPortal &&
        document.postClosingCondition &&
        isCombinedTaskFrameworkEnabled) {
        actionList.push({
          icon: Info,
          name: 'View Post-Closing Condition',
          action: () => setIsViewPostClosingConditionOpen(true),
        });
      }
      if (globalStore.isInternalPortal &&
        isCombinedTaskFrameworkEnabled &&
        document.postClosingCondition &&
        document.status !== TaskStatus.ACCEPTED) {
        actionList.push({
          icon: CheckCircle,
          name: 'Accept Post-Closing Condition',
          action: () => setIsAcceptPostClosingConditionOpen(true),
        });
        actionList.push({
          icon: Cancel,
          name: 'Reject Post-Closing Condition',
          action: () => setIsMoreInfoPostClosingConditionOpen(true),
        });
      }
      if (document.taskId &&
        document.closingCondition &&
        isCombinedTaskFrameworkEnabled) {
        actionList.push({
          icon: Info,
          name: 'View Closing Condition',
          action: () => setIsViewClosingConditionOpen(true),
        });
      }

      if (globalStore.isInternalPortal &&
        document.taskId &&
        document.closingCondition &&
        document.status !== TaskStatus.ACCEPTED &&
        ((documentStore.currentClosingCondition?.level === 1 && globalStore.userFeatures?.closingEntitlements?.closingConditionAdmin)
          || globalStore.userFeatures?.closingEntitlements?.closingConditionCommittee)) {
        actionList.push({
          icon: CheckCircle,
          name: 'Complete Closing Condition',
          action: () => setIsConfirmClosingConditionOpen(true),
        });
      }

      if (globalStore.isInternalPortal &&
        document.taskId &&
        document.closingCondition &&
        document.status !== TaskStatus.ACCEPTED &&
        (globalStore.userFeatures?.closingEntitlements?.closingConditionAdmin || globalStore.userFeatures?.closingEntitlements?.closingConditionCommittee)) {
        actionList.push({
          icon: Cancel,
          name: 'Reject Closing Condition',
          action: () => setIsRejectClosingConditionOpen(true),
        });
      }
      if (globalStore.isInternalPortal) {
        actionList.push({
          icon: Info,
          name: 'View Task Id',
          action: () => setShowLoanDocumentId(true),
        });
      }
    }

    if (
      getRealValPhotoSourceTaskCodes().includes(document.taskName as any) &&
      userStore.isAllowedToChangeRealValPhotoSource
    ) {
      actionList.push({
        icon: PhotoCamera,
        name: 'Change RealVal Photo Source',
        action: () => setChangeRealValSourceOpen(true),
      });
    }

    return actionList;
  };

  const documentActions = useMemo(getDocumentActions, [
    document,
    onUpload,
    onDownload,
    onOpenForm,
  ]);

  return documentActions.length ? (
    <>
      <DropdownMenu closingConditionId={document.closingCondition?.closingConditionId} options={documentActions}>
        <MoreHorizIcon />
      </DropdownMenu>
      <DocumentHistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        document={document}
      />
      <AlertDialog
        open={!!showLoanDocumentId}
        title="Task Id"
        body={document.taskId}
        handleOk={() => setShowLoanDocumentId(false)}
        handleClose={() => setShowLoanDocumentId(false)}
      />
      {isUploadOpen && document?.taskId ?
        <CustomTaskDialog
          title={document?.taskName}
          taskId={document?.taskId}
          open={isUploadOpen}
          dialogContent={
            shouldHideTaskUploadTab(document) ? null : (
              <LineItemUpload
                document={documentStore.formatDocument(document, loanProcess ?? LoanProcess.UNDERWRITING)}
                expand={true}
              />
            )
          }
          handleClose={() => {
            setUploadOpen(false);
            onClose ? onClose() : fetchTodos(document?.loanId, loanProcess);
          }}
        />
        :
        <FileUploadModal
          title={`Upload: ${document.taskName}`}
          open={isUploadOpen}
          fileTypes={document.taskName === DocumentName.TAKE_INTERNAL_VALUATION_PHOTOS_APPRAISAL ? ['pdf'] : undefined}
          onClose={() => setUploadOpen(false)}
          onUpload={(uploads: FileUpload[]) =>
            onUpload(uploads[0].file, document)
          }
        />
      }
      <InformationDialog
        open={isViewClosingConditionOpen}
        title="Closing Condition"
        handleClose={() => setIsViewClosingConditionOpen(false)}
        body={<>
          <Box>
            <strong>Title:</strong> {document.closingCondition?.title} <br />
            <strong>Description:</strong> {document.closingCondition?.description}
          </Box>
        </>}
      />
      <InformationDialog
        open={isViewPostClosingConditionOpen}
        title="Post-Closing Condition"
        handleClose={() => setIsViewPostClosingConditionOpen(false)}
        body={<>
          <Box>
            <strong>Title:</strong> {document.postClosingCondition?.title} <br />
            <strong>Description:</strong> {document.postClosingCondition?.description}
          </Box>
        </>}
      />
      <RenameModal
        open={isRenameOpen}
        onClose={() => setIsRenameOpen(false)}
        document={document}
        loanId={document.loanId}
        loanProcess={loanProcess}
      />
      <DeleteModal
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        document={document}
        loanId={document.loanId}
        loanProcess={loanProcess}
      />
      <MoveDocumentModal
        open={isMoveOpen}
        onClose={() => setIsMoveOpen(false)}
        document={document}
        loanId={document.loanId}
        loanProcess={loanProcess}
      />
      <SingleTaskView
        taskId={task}
        onClose={handleCloseTask}
      />
      <CreateClosingConditionDialog
        onClose={() => handleCloseClosingCondition()}
        open={isClosingConditionOpen}
        taskId={document?.taskId}
        onSuccess={handleConfirmCreateClosingCondition}
        store={documentStore.documentFormStore.createClosingConditionStore}
      />
      <CreatePostClosingConditionDialog
        open={isPostClosingConditionOpen}
        taskId={document?.taskId}
        taskTitle={document?.taskName}
        onClose={() => handleClosePostClosingCondition()}
        onSuccess={() => handleClosePostClosingCondition()}
        store={documentStore.documentFormStore.createPostClosingConditionStore}
      />
      <ResolveClosingConditionDialog
        title="Accept Post-Closing Condition"
        message="Are you sure you want to accept this Post-Closing Condition?"
        closingConditionTitle={document?.postClosingCondition?.title}
        closingConditionDescription={document?.postClosingCondition?.description || ''}
        open={isAcceptPostClosingConditionOpen}
        onConfirm={(comment) => resolvePostClosingCondition(true, comment)}
        onClose={() => setIsAcceptPostClosingConditionOpen(false)}
      />
      <ResolveClosingConditionDialog
        title="Reject Post-Closing Condition"
        message="Are you sure you want to reject this Post-Closing Condition?"
        closingConditionTitle={document?.postClosingCondition?.title}
        closingConditionDescription={document?.postClosingCondition?.description || ''}
        open={isMoreInfoPostClosingConditionOpen}
        onConfirm={(comment) => resolvePostClosingCondition(false, comment)}
        onClose={() => setIsMoreInfoPostClosingConditionOpen(false)}
        commentRequired
      />
      <ResolveClosingConditionDialog
        title="Complete Closing Condition"
        message="Are you sure you want to complete this Closing Condition?"
        closingConditionTitle={document?.closingCondition?.title}
        closingConditionDescription={document?.closingCondition?.description}
        open={isConfirmClosingConditionOpen}
        onConfirm={(comment) => completeClosingCondition(document?.loanId, document?.taskId, comment)}
        onClose={() => handleCloseConfirmClosingCondition()}
      />
      <ResolveClosingConditionDialog
        title="Reject Closing Condition"
        message="Are you sure you want to reject this Closing Condition?"
        closingConditionTitle={document?.closingCondition?.title}
        closingConditionDescription={document?.closingCondition?.description}
        open={isRejectClosingConditionOpen}
        onConfirm={(comment) => rejectClosingCondition(document?.loanId, document?.taskId, comment)}
        onClose={() => handleCloseRejectClosingCondition()}
        commentRequired
      />
      <ConfirmDialog
        handleConfirm={handleSubmitConfirmModal}
        handleClose={() => setConfirmModalAction(null)}
        handleCancel={() => setConfirmModalAction(null)}
        open={confirmModalAction !== null}
        body={getConfirmModalBody()}
        confirmButtonText="Confirm"
        cancelButtonText="Close"
        title={'Confirmation'}
      />
      <CompletionNoteDialog
        open={showCompletionNoteModal}
        onClose={() => setShowCompletionNoteModal(false)}
        onConfirm={async (completionNote) => {
          const propertyId = document.collateralId ?? document.typeId2;
          if (!propertyId) {
            globalStore.notificationStore.showErrorNotification({
              message: 'Unable to complete task: property ID is missing.',
            });
            return;
          }
          try {
            await valuationDisputeService.saveCompletionNote(
              document.taskId,
              document.loanId,
              propertyId,
              document.title,
              completionNote
            );
            setShowCompletionNoteModal(false);
            onCloseAction();
          } catch (e) {
            globalStore.notificationStore.showErrorNotification({
              message: 'Failed to save completion note. Please try again.',
            });
          }
        }}
      />
      <InformationDialog
        open={informationModalText !== ''}
        title="Action Unavailable"
        handleClose={() => setInformationModalText('')}
        body={<Typography className={classes.informationDialogText}>{informationModalText}</Typography>}
      />
      <AssignToDialog
        open={showAssignToModal}
        loanId={document?.loanId}
        taskId={document?.taskId}
        taskName={document?.taskName}
        readOnly={document?.readOnly}
        propertyId={typeId2}
        handleClose={handleCloseAssignToModal}
      />
      <AssignToOwnerDialog
        open={showAssignToOwnerModal}
        currentStatus={document?.status}
        taskId={document?.taskId}
        handleClose={handleCloseAssignToOwnerModal}
      />
      <ChangeRealValPhotoSourceDialog
        open={isChangeRealValSourceOpen}
        document={document}
        typeId2={typeId2}
        onClose={() => {
          setChangeRealValSourceOpen(false);
          onClose ? onClose() : fetchTodos(document?.loanId, loanProcess);
        }}
      />
    </>
  ) : null;
});

export const TodoTitleLink = observer(({ children, document, loanProcess, showUploadLink = false, init = undefined, onClose = undefined, onUploadCustom = undefined, style = undefined, propertyId = undefined }) => {
  const { globalStore } = useBaseStore();
  const { documentStore } = useDocumentStore();
  const [isUploadOpen, setUploadOpen] = useState(false);
  const classes = useStyles();
  const loanId = document.loanId;

  const { loanStore } = useLoanStore();
  const [informationModalText, setInformationModalText] = useState('');
  const [showAssignToModal, setShowAssignToModal] = useState<boolean>(false);
  const actionsUnavailable = areActionsUnavailableBasedOnDocument(document!);

  const onUpload = async (file, doc) => {
    init && init();
    await documentStore.uploadDocument(
      file,
      doc,
      loanProcess,
      null,
      document.drawId
    );
    fetchTodos(loanId, loanProcess);
  };

  const fetchTodos = (loanId, loanProcess) => {
    if (loanProcess === LoanProcess.TODOS) {
      documentStore.loanTodosStore.fetchTodosForMultipleLoanProcesses(loanId, loanProcess, [LoanProcess.UNDERWRITING, LoanProcess.CLOSING]);
    } else if (loanProcess === LoanProcess.PAYOFF) {
      documentStore.loanTodosStore.fetchTodos(loanId, loanProcess, TaskV2Type.PAYOFF_ID, document?.payoffId);
    } else {
      documentStore.loanTodosStore.fetchTodos(loanId, loanProcess, TaskV2Type.DRAW_ID, document?.drawId);
    }
  }

  const onDownload = doc => {
    init && init();
    const isPDF = document.dropboxPath.endsWith('.pdf');
    if (isPDF) {
      documentStore.previewDocument(doc);
    } else {
      documentStore.downloadDocument(document);
    }
  };

  const handleOpenInternalValuation = () => {
    if (actionsUnavailable) {
      setInformationModalText(TASK_IN_PROGRESS_MESSAGE);
    } else {
      setShowAssignToModal(true);
    }
  }

  const handleCloseAssignToModal = () => {
    setShowAssignToModal(false);
    loanStore.fetchLoanDetails(document!.loanId);
    fetchTodos(loanId, loanProcess);
  }

  if (document.manualTask) {
    return <Link
      className={classes.link}
      onClick={() => {
        init && init();
        documentStore.onOpenForm(document);
      }}
      style={style}
    >
      {children}
    </Link>
  }
  if (showOpen(document, globalStore)) {
    return (
      <>
        <Link
          className={classes.link}
          onClick={() => {
            init && init();
            if (document.taskName === DocumentName.INTERNAL_VALUATION_REPORT && document?.readOnly && document.status === TaskStatus.NOT_APPLICABLE) {
              handleOpenInternalValuation();
            } else {
              documentStore.onOpenForm(document);
            }
          }}
          style={style}
        >
          {children}
        </Link>
        <InformationDialog
          open={informationModalText !== ''}
          title="Action Unavailable"
          handleClose={() => setInformationModalText('')}
          body={<Typography className={classes.informationDialogText}>{informationModalText}</Typography>}
        />
        <AssignToDialog
          open={showAssignToModal}
          loanId={document?.loanId!}
          taskId={document?.taskId!}
          taskName={document?.taskName as DocumentName}
          readOnly={document?.readOnly}
          propertyId={propertyId}
          handleClose={handleCloseAssignToModal}
        />
      </>
    );
  } else if (document.canUploadFile && (!globalStore.isInternalPortal || showUploadLink)) {
    return (
      <>
        <Link className={classes.link}
          onClick={() => {
            if (onUploadCustom) {
              onUploadCustom();
            } else {
              setUploadOpen(true)
            }
          }}
          style={style}
        >
          {children}
        </Link>
        {isUploadOpen && document?.taskId ?
          <CustomTaskDialog
            title={document?.taskName}
            taskId={document?.taskId}
            open={isUploadOpen}
            dialogContent={
              shouldHideTaskUploadTab(document) ? null : (
                <LineItemUpload
                  document={documentStore.formatDocument(document, loanProcess ?? LoanProcess.UNDERWRITING)}
                  expand={true}
                />
              )
            }
            handleClose={() => {
              setUploadOpen(false);
              onClose ? onClose() : fetchTodos(document?.loanId, loanProcess);
            }}
          />
          : <FileUploadModal
            title={`Upload: ${document.taskName}`}
            open={isUploadOpen}
            fileTypes={document.taskName === DocumentName.TAKE_INTERNAL_VALUATION_PHOTOS_APPRAISAL ? ['pdf'] : undefined}
            onClose={() => {
              onClose && onClose();
              setUploadOpen(false);
            }}
            onUpload={(uploads: FileUpload[]) =>
              onUpload(uploads[0].file, document)
            }
          />
        }
      </>
    );
  } else if (showDownload(document)) {
    return (
      <Link className={classes.link}
        onClick={() => onDownload(document)}
        style={style}
      >
        {children}
      </Link>
    );
  } else {
    return children;
  }
});

export const DownloadTodoButton = observer(({ document }) => {
  const { documentStore } = useDocumentStore();
  const classes = useStyles();

  const onDownload = () => {
    documentStore.downloadDocument(document);
  };

  if (showDownload(document)) {
    return (
      <IconButton
        onClick={onDownload}
        className={classes.downloadTodo}
        size="small"
      >
        <GetApp />
      </IconButton>
    );
  } else {
    return null;
  }
});
